import React, { useState, useEffect, useRef } from "react";
import usePageTitle from '../../hooks/usePageTitle';
import { useNavigate } from "react-router-dom";
import "./LandingPage.css";

const stepAccount = `${process.env.PUBLIC_URL}/illustrations/step-account.svg`;
const stepBusiness = `${process.env.PUBLIC_URL}/illustrations/step-business.svg`;
const stepCatalogue = `${process.env.PUBLIC_URL}/illustrations/step-catalogue.svg`;
const stepInvoice = `${process.env.PUBLIC_URL}/illustrations/step-invoice.svg`;
const gstReports = `${process.env.PUBLIC_URL}/illustrations/gst-reports.svg`;
const whoTraders = `${process.env.PUBLIC_URL}/illustrations/who-traders.svg`;
const whoManufacturers = `${process.env.PUBLIC_URL}/illustrations/who-manufacturers.svg`;
const whoRetailers = `${process.env.PUBLIC_URL}/illustrations/who-retailers.svg`;
const whoServices = `${process.env.PUBLIC_URL}/illustrations/who-services.svg`;
const ctaBanner = `${process.env.PUBLIC_URL}/illustrations/cta-banner.svg`;

const prefersReducedMotion = () =>
  typeof window !== "undefined" && window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// ── Reveal: adds .is-in to every .ap-reveal once it scrolls into view ──────────
function useReveal(rootRef) {
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const els = root.querySelectorAll(".ap-reveal");
    if (!("IntersectionObserver" in window)) { els.forEach(el => el.classList.add("is-in")); return; }
    const obs = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (e.isIntersecting) { e.target.classList.add("is-in"); obs.unobserve(e.target); }
      });
    }, { threshold: 0.12, rootMargin: "0px 0px -40px 0px" });
    els.forEach(el => obs.observe(el));
    return () => obs.disconnect();
  }, [rootRef]);
}

// ── Counter: eases out (no overshoot), skips straight to value on reduced motion
function Counter({ target, suffix = "" }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    let raf;
    const obs = new IntersectionObserver(([e]) => {
      if (!e.isIntersecting) return;
      obs.disconnect();
      if (prefersReducedMotion()) { setCount(target); return; }
      const start = performance.now();
      const dur = 1400;
      const tick = (now) => {
        const t = Math.min(1, (now - start) / dur);
        const eased = 1 - Math.pow(1 - t, 4);
        setCount(Math.round(target * eased));
        if (t < 1) raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
    }, { threshold: 0.5 });
    if (ref.current) obs.observe(ref.current);
    return () => { obs.disconnect(); cancelAnimationFrame(raf); };
  }, [target]);
  return <span ref={ref}>{count.toLocaleString("en-IN")}{suffix}</span>;
}

// ── Line icons (SF Symbols–style strokes) ─────────────────────────────────────
const ICON_PATHS = {
  invoice: "M7 3h7l5 5v13H7z M14 3v5h5 M10 13h6 M10 17h6",
  box: "M3 7.5 12 3l9 4.5-9 4.5z M3 7.5v9L12 21l9-4.5v-9 M12 12v9",
  chart: "M4 20h16 M7 16v-5 M12 16V6 M17 16v-8",
  users: "M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z M2 21v-1a7 7 0 0 1 14 0v1 M16 3.5a4 4 0 0 1 0 7.5 M22 21v-1a6 6 0 0 0-4-5.6",
  clipboard: "M9 3h6v4H9z M8 5H5v16h14V5h-3 M9 12h6 M9 16h4",
  rupee: "M6 4h12 M6 9h12 M9 4a5 5 0 0 1 0 10H6l8 7",
  bank: "M3 10 12 4l9 6 M5 10v8 M9.5 10v8 M14.5 10v8 M19 10v8 M3 21h18",
  ledger: "M5 3h12a2 2 0 0 1 2 2v16H7a2 2 0 0 1-2-2z M5 19a2 2 0 0 1 2-2h12 M9 7h6 M9 11h6",
  import: "M12 3v12 M7 10l5 5 5-5 M4 21h16",
};
function Icon({ name, size = 22 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d={ICON_PATHS[name]} />
    </svg>
  );
}

// ── iOS app-icon tiles: system-colour gradient + filled white glyph ───────────
// Detail strokes use var(--tint) (the tile's darker stop) so they read as cut-outs.
const T = { stroke: "var(--tint)", strokeWidth: 1.7, strokeLinecap: "round", strokeLinejoin: "round", fill: "none" };
const IOS_GLYPHS = {
  invoice: <>
    <path d="M6.5 2.5h7.8l4.7 4.7v13.3a1.5 1.5 0 0 1-1.5 1.5h-11A1.5 1.5 0 0 1 5 20.5v-16.5a1.5 1.5 0 0 1 1.5-1.5z" fill="#fff" />
    <path d="M14.3 2.5v3.7a1 1 0 0 0 1 1H19z" fill="#fff" opacity=".55" />
    <path d="M8.3 11.5h7.4M8.3 15h7.4M8.3 18.5h4.4" {...T} />
  </>,
  box: <>
    <path d="M12 2.6 20.6 7 12 11.4 3.4 7z" fill="#fff" />
    <path d="M3.4 8.6 11.2 12.6v9L3.4 17.6z" fill="#fff" opacity=".85" />
    <path d="M20.6 8.6 12.8 12.6v9l7.8-4z" fill="#fff" opacity=".62" />
  </>,
  chart: <>
    <rect x="3.5" y="12.5" width="4.2" height="8.5" rx="1.3" fill="#fff" opacity=".72" />
    <rect x="9.9" y="7.5" width="4.2" height="13.5" rx="1.3" fill="#fff" opacity=".86" />
    <rect x="16.3" y="3" width="4.2" height="18" rx="1.3" fill="#fff" />
  </>,
  users: <>
    <circle cx="16.8" cy="8.6" r="2.9" fill="#fff" opacity=".65" />
    <path d="M15.6 13.4c3.4-.4 6.4 1.7 6.4 5.1v1h-4.8c0-2.5-.5-4.4-1.6-6.1z" fill="#fff" opacity=".65" />
    <circle cx="9" cy="7.8" r="3.8" fill="#fff" />
    <path d="M2 19.4c0-3.8 3.1-6.3 7-6.3s7 2.5 7 6.3v.6H2z" fill="#fff" />
  </>,
  clipboard: <>
    <rect x="4.5" y="4" width="15" height="18" rx="2.4" fill="#fff" />
    <rect x="8.3" y="2.3" width="7.4" height="4" rx="1.4" fill="#fff" stroke="var(--tint)" strokeWidth="1.3" />
    <path d="M8 12.3l1.6 1.6 2.9-3M14.3 12.2h1.8M8 17.2h8" {...T} />
  </>,
  rupee: <>
    <circle cx="12" cy="12" r="9.6" fill="#fff" />
    <path d="M8.6 7.4h6.8M8.6 10.4h6.8M10.2 7.4a3 3 0 0 1 0 6H8.6l5.2 4.2" {...T} strokeWidth="1.8" />
  </>,
  bank: <>
    <path d="M12 2.3 21 7.2V9H3V7.2z" fill="#fff" />
    <rect x="5.2" y="10.3" width="2.6" height="7" rx=".7" fill="#fff" opacity=".9" />
    <rect x="10.7" y="10.3" width="2.6" height="7" rx=".7" fill="#fff" opacity=".9" />
    <rect x="16.2" y="10.3" width="2.6" height="7" rx=".7" fill="#fff" opacity=".9" />
    <rect x="3" y="18.5" width="18" height="2.8" rx=".9" fill="#fff" />
  </>,
  ledger: <>
    <path d="M5 3.8A1.8 1.8 0 0 1 6.8 2H19.5v15.5H6.8A1.8 1.8 0 0 0 5 19.3z" fill="#fff" />
    <path d="M5 19.3a1.8 1.8 0 0 1 1.8-1.8h12.7V22H6.8A1.8 1.8 0 0 1 5 20.2z" fill="#fff" opacity=".6" />
    <path d="M9 7h6.5M9 10.5h6.5" {...T} />
  </>,
  import: <>
    <path d="M2.8 13.6h4.8l1.6 2.6h5.6l1.6-2.6h4.8v4.9a2.3 2.3 0 0 1-2.3 2.3H5.1a2.3 2.3 0 0 1-2.3-2.3z" fill="#fff" />
    <path d="M12 2.8v9.4M8.4 8.7 12 12.3l3.6-3.6" stroke="#fff" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round" fill="none" />
  </>,
};
// iOS system colours, light top stop → deeper bottom stop
const IOS_TINTS = {
  invoice: ["#3d9bff", "#0a6cff"],    // Blue
  box: ["#ffb340", "#ff8a00"],        // Orange
  chart: ["#4cd964", "#28a745"],      // Green
  users: ["#7d7aff", "#4f4ce0"],      // Indigo
  clipboard: ["#5ac8fa", "#1a9fd8"],  // Cyan
  rupee: ["#ff5f7e", "#e8264f"],      // Pink
  bank: ["#a0a0a8", "#6c6c74"],       // Graphite (Settings)
  ledger: ["#c86cf5", "#9a3fd1"],     // Purple
  import: ["#2fd6c8", "#00a69a"],     // Mint
};
function IOSIcon({ name, size = 56 }) {
  const [top, bottom] = IOS_TINTS[name];
  return (
    <div className="ap-ios-icon" aria-hidden="true"
      style={{ "--top": top, "--tint": bottom, width: size, height: size }}>
      <svg width={size * 0.58} height={size * 0.58} viewBox="0 0 24 24">{IOS_GLYPHS[name]}</svg>
    </div>
  );
}
const Check = ({ size = 12, w = 2.2 }) => (
  <svg width={size} height={size} viewBox="0 0 12 12" fill="none" aria-hidden="true">
    <path d="M2.5 6.2 5 8.5l4.5-5" stroke="currentColor" strokeWidth={w} strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const Chevron = () => (
  <svg width="9" height="9" viewBox="0 0 10 10" fill="none" aria-hidden="true">
    <path d="M3.5 1.5 7 5 3.5 8.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// ── Invoice mockup ────────────────────────────────────────────────────────────
// Total ticks up after the line items have slid in (instant on reduced motion)
function TotalTicker({ value, delay = 1000, duration = 900 }) {
  const [n, setN] = useState(prefersReducedMotion() ? value : 0);
  useEffect(() => {
    if (prefersReducedMotion()) { setN(value); return; }
    let raf, start;
    const timer = setTimeout(() => {
      const tick = (now) => {
        if (!start) start = now;
        const t = Math.min(1, (now - start) / duration);
        setN(Math.round(value * (1 - Math.pow(1 - t, 3))));
        if (t < 1) raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
    }, delay);
    return () => { clearTimeout(timer); cancelAnimationFrame(raf); };
  }, [value, delay, duration]);
  return <>₹{n.toLocaleString("en-IN")}</>;
}

// Decorative UPI QR (finder squares + deterministic modules)
function MiniQR({ size = 46 }) {
  const N = 21;
  const finder = (r, c) => {
    for (const [fr, fc] of [[0, 0], [0, N - 7], [N - 7, 0]]) {
      const y = r - fr, x = c - fc;
      if (y >= 0 && y < 7 && x >= 0 && x < 7) {
        return y === 0 || y === 6 || x === 0 || x === 6 || (y >= 2 && y <= 4 && x >= 2 && x <= 4) ? 1 : 0;
      }
    }
    return -1;
  };
  const cells = [];
  for (let r = 0; r < N; r++) for (let c = 0; c < N; c++) {
    const f = finder(r, c);
    const on = f === -1 ? ((r * 7 + c * 13 + r * c) % 5 < 2) : f === 1;
    if (on) cells.push(<rect key={`${r}-${c}`} x={c} y={r} width="1" height="1" />);
  }
  return <svg width={size} height={size} viewBox={`0 0 ${N} ${N}`} shapeRendering="crispEdges" fill="#1d1d1f">{cells}</svg>;
}

function InvoiceMockup() {
  // Taxable 39,400 + CGST 9% 3,546 + SGST 9% 3,546 = 46,492
  const items = [
    { name: "LED Panel 40W", hsn: "9405", qty: 20, rate: 850 },
    { name: "Copper Wire 90m", hsn: "8544", qty: 8, rate: 1800 },
    { name: "MCB Switch 32A", hsn: "8536", qty: 25, rate: 320 },
  ];
  const inr = (v) => `₹${v.toLocaleString("en-IN")}`;
  return (
    <div className="ap-invoice" aria-hidden="true">
      <div className="ap-inv-band" />
      <div className="ap-inv-top">
        <div className="ap-inv-from">
          <div className="ap-inv-logo">ST</div>
          <div>
            <div className="ap-inv-co">Sharma Traders Pvt. Ltd.</div>
            <div className="ap-inv-meta">Andheri East, Mumbai 400069</div>
            <div className="ap-inv-meta">GSTIN 27AAACS4321K1Z8</div>
          </div>
        </div>
        <div className="ap-inv-id">
          <div className="ap-inv-label">Tax Invoice</div>
          <div className="ap-inv-num">INV-2026-0042</div>
          <div className="ap-inv-meta">3 Mar 2026</div>
        </div>
      </div>

      <div className="ap-inv-parties">
        <div>
          <div className="ap-inv-k">Bill to</div>
          <div className="ap-inv-v">Mehta Electronics</div>
          <div className="ap-inv-meta">GSTIN 27AAFCM8890R1Z2</div>
        </div>
        <div>
          <div className="ap-inv-k">Place of supply</div>
          <div className="ap-inv-v">27 – Maharashtra</div>
          <div className="ap-inv-meta">Due 18 Mar 2026</div>
        </div>
      </div>

      <div className="ap-inv-table">
        <div className="ap-inv-row ap-inv-head"><span>Item</span><span>HSN</span><span>Qty</span><span>Amount</span></div>
        {items.map((it, i) => (
          <div className="ap-inv-row ap-inv-line" style={{ "--i": i }} key={it.name}>
            <span><b>{it.name}</b><small>@ {inr(it.rate)}</small></span>
            <span>{it.hsn}</span>
            <span>{it.qty}</span>
            <span>{inr(it.qty * it.rate)}</span>
          </div>
        ))}
      </div>

      <div className="ap-inv-sum">
        <div><span>Taxable value</span><span>₹39,400</span></div>
        <div><span>CGST @ 9%</span><span>₹3,546</span></div>
        <div><span>SGST @ 9%</span><span>₹3,546</span></div>
        <div className="ap-inv-grand"><span>Total</span><span><TotalTicker value={46492} /></span></div>
      </div>

      <div className="ap-inv-foot">
        <div className="ap-inv-upi">
          <div className="ap-inv-qr"><MiniQR /></div>
          <div>
            <div className="ap-inv-v" style={{ fontSize: 11 }}>Scan to pay via UPI</div>
            <div className="ap-inv-meta">sharmatraders@okhdfc</div>
          </div>
        </div>
        <div className="ap-inv-sign">
          <svg width="64" height="20" viewBox="0 0 64 20" fill="none">
            <path d="M2 14c5-9 8-11 9-6s-2 9 2 6 6-10 9-7-1 9 3 7 5-6 8-5 2 5 6 4 8-4 12-3 6 1 9 0" stroke="#1565c0" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <div className="ap-inv-meta">Authorised signatory</div>
        </div>
      </div>

      <div className="ap-inv-stamp">Paid</div>
    </div>
  );
}

// Pointer-driven 3D tilt with a critically damped spring (interruptible, no overshoot).
// Chips with data-depth shift with the same spring for parallax.
function useTilt(stageRef) {
  useEffect(() => {
    const stage = stageRef.current;
    if (!stage || prefersReducedMotion()) return;
    if (!window.matchMedia || !window.matchMedia("(hover: hover) and (pointer: fine)").matches) return;
    const card = stage.querySelector(".ap-stage-card");
    const layers = Array.from(stage.querySelectorAll("[data-depth]"));
    const zone = stage.closest(".ap-hero") || stage;
    const w = (2 * Math.PI) / 0.55;          // response ≈ 0.55s
    const k = w * w, c = 2 * w;              // damping ratio 1.0
    let tx = 0, ty = 0, x = 0, y = 0, vx = 0, vy = 0, raf = null, last = 0;

    const step = (now) => {
      const dt = last ? Math.min(0.032, (now - last) / 1000) : 0.016;
      last = now;
      vx += (k * (tx - x) - c * vx) * dt; vy += (k * (ty - y) - c * vy) * dt;
      x += vx * dt; y += vy * dt;
      card.style.transform = `perspective(1100px) rotateY(${(x * 7).toFixed(3)}deg) rotateX(${(-y * 6).toFixed(3)}deg)`;
      layers.forEach(el => { const d = +el.dataset.depth; el.style.translate = `${(x * d).toFixed(2)}px ${(y * d).toFixed(2)}px`; });
      if (Math.abs(tx - x) + Math.abs(ty - y) + Math.abs(vx) + Math.abs(vy) > 0.0008) raf = requestAnimationFrame(step);
      else { raf = null; last = 0; }
    };
    const kick = () => { if (!raf) raf = requestAnimationFrame(step); };
    const onMove = (e) => {
      const r = stage.getBoundingClientRect();
      tx = Math.max(-1, Math.min(1, ((e.clientX - r.left) / r.width) * 2 - 1));
      ty = Math.max(-1, Math.min(1, ((e.clientY - r.top) / r.height) * 2 - 1));
      kick();
    };
    const onLeave = () => { tx = 0; ty = 0; kick(); };
    zone.addEventListener("pointermove", onMove);
    zone.addEventListener("pointerleave", onLeave);
    return () => {
      zone.removeEventListener("pointermove", onMove);
      zone.removeEventListener("pointerleave", onLeave);
      cancelAnimationFrame(raf);
    };
  }, [stageRef]);
}

// ── FAQ item ──────────────────────────────────────────────────────────────────
function FAQItem({ q, a, id }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`ap-faq-item${open ? " is-open" : ""}`}>
      <button className="ap-faq-q" onClick={() => setOpen(o => !o)}
        aria-expanded={open} aria-controls={`faq-${id}`}>
        <span>{q}</span>
        <span className="ap-faq-sign" aria-hidden="true" />
      </button>
      <div className="ap-faq-a" id={`faq-${id}`} role="region">
        <div><p>{a}</p></div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// LANDING PAGE
// ════════════════════════════════════════════════════════════════════════════
export default function LandingPage() {
  usePageTitle('Welcome');
  const navigate = useNavigate();
  const rootRef = useRef(null);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 8);
    fn();
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);

  useReveal(rootRef);
  const stageRef = useRef(null);
  useTilt(stageRef);

  const FEATURES = [
    { icon: "invoice", title: "GST-Ready Invoicing", desc: "CGST, SGST and IGST auto-calculated. B2B and B2C invoices, credit notes, and delivery challans all in one place." },
    { icon: "box", title: "Stock & Inventory", desc: "Real-time stock tracking with purchase orders, GRN, and automatic stock updates on every sale and purchase." },
    { icon: "chart", title: "Financial Reports", desc: "P&L, cash flow, balance sheet and ageing reports — everything your CA needs, ready to export at any time." },
    { icon: "users", title: "Multi-User Ready", desc: "Built for teams from day one. Multiple users can work simultaneously with role-based access coming soon." },
    { icon: "clipboard", title: "Purchase Management", desc: "Raise POs, receive goods, track supplier payments. Full procure-to-pay workflow with supplier ledger." },
    { icon: "rupee", title: "Sales & Commission", desc: "Manage your sales team, track performance by salesperson, and auto-calculate commissions against invoices." },
    { icon: "bank", title: "Bank & UPI Integration", desc: "Add bank details once. They appear on every invoice with a UPI QR code so customers can pay instantly." },
    { icon: "ledger", title: "Client Ledger", desc: "Full transaction history per client — invoices raised, payments received, outstanding balances at a glance." },
    { icon: "import", title: "Bulk Import", desc: "Import your existing product catalogue via CSV in seconds. No manual entry for large inventories." },
  ];

  const STEPS = [
    { art: stepAccount, title: "Create your account", desc: "Sign up in 30 seconds. Enter your name and email — no business details required upfront." },
    { art: stepBusiness, title: "Set up your business", desc: "Complete the onboarding wizard. Add your GSTIN, PAN, logo, and invoice preferences once." },
    { art: stepCatalogue, title: "Add products & clients", desc: "Build your product catalogue and client list. Both auto-fill on every invoice you raise." },
    { art: stepInvoice, title: "Start invoicing", desc: "Create a professional GST invoice in under 2 minutes. Share via link or download as a PDF." },
  ];

  const GST_FEATURES = [
    { label: "GSTR-1 Ready", desc: "B2B and B2C invoice data auto-organised by tax rate, HSN code, and filing period." },
    { label: "GSTR-3B Ready", desc: "Output tax, ITC, and net payable calculated with IGST cross-utilization handled correctly." },
    { label: "HSN Summary", desc: "Item-wise HSN summary with taxable value, CGST, SGST, and IGST totals for each code." },
    { label: "E-Invoice Ready", desc: "Invoice format structured for IRN generation via IRP — e-invoice support on the roadmap." },
    { label: "Reverse Charge", desc: "Mark invoices as RCM applicable. Separate tracking for reverse charge liability." },
    { label: "Inter & Intra-State", desc: "Automatically applies IGST for inter-state and CGST+SGST for intra-state transactions." },
  ];

  const WHO = [
    { art: whoTraders, title: "Traders & Distributors", desc: "Built for high-volume B2B trade. Purchase orders, supplier management, and stock tracking built in." },
    { art: whoManufacturers, title: "Manufacturers", desc: "Track raw material purchases and finished goods inventory. Full production-to-sale workflow." },
    { art: whoRetailers, title: "Retailers", desc: "Fast invoice creation for walk-in customers. Stock depletion on every sale, reorder alerts coming soon." },
    { art: whoServices, title: "Service Businesses", desc: "Service invoices with SAC codes, TDS tracking, and professional PDF delivery to clients." },
  ];

  const TESTIMONIALS = [
    { quote: "We switched from Tally to EasyBilling and haven't looked back. The GST reports alone save us 3 hours every month.", name: "Rakesh Gupta", role: "Owner", company: "Gupta Electricals, Delhi", initials: "RG" },
    { quote: "Creating invoices used to take 10 minutes each. Now it's under 2. The auto-fill for clients and products is a game changer.", name: "Priya Mehta", role: "Finance Manager", company: "Mehta Pharma, Mumbai", initials: "PM" },
    { quote: "The purchase order workflow is exactly what we needed. Stock updates automatically when goods arrive — no manual entry.", name: "Sunil Agarwal", role: "Director", company: "Agarwal Traders, Surat", initials: "SA" },
  ];

  const COMPARISON = [
    ["GST-ready invoicing", true, true, false],
    ["Cloud-based access", true, false, true],
    ["One-click GST reports", true, false, false],
    ["Purchase order workflow", true, true, false],
    ["Stock management", true, true, false],
    ["No installation needed", true, false, true],
    ["Multi-user support", true, true, false],
    ["Under ₹5000/year", true, false, true],
  ];

  const FAQS = [
    { q: "Is EasyBilling compliant with Indian GST regulations?", a: "Yes. EasyBilling is built specifically for Indian businesses. It handles CGST, SGST, IGST, reverse charge, and generates GSTR-1 and GSTR-3B ready reports. HSN/SAC codes are supported on every invoice." },
    { q: "Can I use EasyBilling if I'm not GST registered?", a: "Absolutely. GSTIN is optional. You can create professional invoices without a GSTIN and add it later once you register." },
    { q: "Is my data secure?", a: "Your data is stored in a private database. Each account is fully isolated. We use bcrypt for password hashing, JWT for session management, and all connections are encrypted." },
    { q: "How many invoices can I create?", a: "There are no limits on invoices, clients, products, or any other feature. Create as much as your business needs." },
    { q: "Can I migrate from Tally or Zoho Books?", a: "You can import your product catalogue via CSV right now. Full data migration tools for clients and historical invoices are on the roadmap." },
    { q: "Does EasyBilling work on mobile?", a: "EasyBilling is a web application that works on any browser including mobile. A dedicated Android and iOS app is on the roadmap." },
    { q: "What happens to my data if I stop using EasyBilling?", a: "Your data belongs to you. You can export all invoices as PDFs and reports as CSV at any time. We do not lock your data." },
  ];

  const d = (ms) => ({ "--d": `${ms}ms` });

  return (
    <div className="ap" ref={rootRef}>

      {/* ── Nav ─────────────────────────────────────────────────────────── */}
      <nav className={`ap-nav${scrolled ? " is-scrolled" : ""}`} aria-label="Main">
        <div className="ap-container ap-nav-inner">
          <button className="ap-brand" onClick={() => window.scrollTo({ top: 0, behavior: prefersReducedMotion() ? "auto" : "smooth" })}>
            <span className="ap-brand-mark">EB</span>
            <span className="ap-brand-name">EasyBilling</span>
          </button>
          <div className="ap-nav-links">
            {[["Features", "#features"], ["How it works", "#how"], ["GST", "#gst"], ["FAQ", "#faq"]].map(([l, h]) => (
              <a key={l} href={h}>{l}</a>
            ))}
          </div>
          <div className="ap-nav-actions">
            <button className="ap-link" onClick={() => navigate("/login")}>Sign in</button>
            <button className="ap-btn ap-btn--primary ap-btn--sm" onClick={() => navigate("/register")}>Get started</button>
          </div>
        </div>
      </nav>

      {/* ── Hero ────────────────────────────────────────────────────────── */}
      <header className="ap-hero">
        <div className="ap-container ap-hero-grid">
        <div className="ap-hero-copy">
          <p className="ap-eyebrow ap-reveal">Built for Indian businesses</p>
          <h1 className="ap-display ap-reveal" style={d(60)}>
            Invoicing that works as hard <span className="ap-grad">as you do.</span>
          </h1>
          <p className="ap-lede ap-reveal" style={d(120)}>
            GST-compliant invoices, purchase orders, stock management and financial
            reports — all in one place. No accountant needed for day-to-day billing.
          </p>
          <div className="ap-hero-ctas ap-reveal" style={d(180)}>
            <button className="ap-btn ap-btn--primary" onClick={() => navigate("/register")}>Start for free</button>
            <button className="ap-btn ap-btn--secondary" onClick={() => navigate("/login")}>Sign in</button>
          </div>
          <p className="ap-hero-note ap-reveal" style={d(220)}>No credit card required · Free to use · GST compliant</p>
        </div>

          <div className="ap-stage ap-reveal" style={d(160)} ref={stageRef}>
            <div className="ap-chip ap-chip--r" data-depth="14">
              <div className="ap-chip-icon" style={{ background: "#e8f6ec", color: "#248a3d" }}><Check size={14} /></div>
              <div><div className="ap-chip-k">Paid via UPI</div><div className="ap-chip-v">₹46,492</div></div>
            </div>
            <div className="ap-chip ap-chip--l" data-depth="20">
              <div className="ap-chip-icon" style={{ background: "#e3f0ff", color: "#1565c0" }}><Icon name="invoice" size={17} /></div>
              <div><div className="ap-chip-k">GST auto-calculated</div><div className="ap-chip-v">CGST + SGST</div></div>
            </div>
            <div className="ap-stage-card"><InvoiceMockup /></div>
          </div>
        </div>
      </header>

      {/* ── Stats ───────────────────────────────────────────────────────── */}
      <section className="ap-container" aria-label="EasyBilling in numbers">
        <div className="ap-stats">
          {[
            { label: "Invoices generated", value: 12000, suffix: "+" },
            { label: "Businesses onboarded", value: 400, suffix: "+" },
            { label: "GST reports exported", value: 3200, suffix: "+" },
            { label: "States covered", value: 28, suffix: "" },
          ].map((s) => (
            <div className="ap-stat" key={s.label}>
              <div className="ap-stat-v"><Counter target={s.value} suffix={s.suffix} /></div>
              <div className="ap-stat-l">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── How it works ────────────────────────────────────────────────── */}
      <section id="how" className="ap-section ap-section--alt">
        <div className="ap-container">
          <div className="ap-head ap-reveal">
            <p className="ap-eyebrow">How it works</p>
            <h2 className="ap-title">Up and running in four steps.</h2>
            <p className="ap-lede">No training. No consultant. No migration headache.</p>
          </div>
          <div className="ap-grid ap-grid--4">
            {STEPS.map((s, i) => (
              <article className="ap-card ap-reveal" style={d(i * 80)} key={s.title}>
                <div className="ap-art"><img src={s.art} alt="" loading="lazy" /></div>
                <div className="ap-step-n">Step {i + 1}</div>
                <h3 className="ap-card-t">{s.title}</h3>
                <p className="ap-card-d">{s.desc}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ── Features ────────────────────────────────────────────────────── */}
      <section id="features" className="ap-section">
        <div className="ap-container">
          <div className="ap-head ap-reveal">
            <p className="ap-eyebrow">Everything you need</p>
            <h2 className="ap-title">Run your entire business from one screen.</h2>
            <p className="ap-lede">Every feature is purpose-built for Indian SMBs — no bloat, no learning curve.</p>
          </div>
          <div className="ap-grid ap-grid--3">
            {FEATURES.map((f, i) => (
              <article className="ap-card ap-card--hover ap-reveal" style={d((i % 3) * 70)} key={f.title}>
                <IOSIcon name={f.icon} />
                <h3 className="ap-card-t">{f.title}</h3>
                <p className="ap-card-d">{f.desc}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ── GST Compliance ──────────────────────────────────────────────── */}
      <section id="gst" className="ap-section ap-section--alt">
        <div className="ap-container">
          <div className="ap-split">
            <div className="ap-reveal">
              <p className="ap-eyebrow">GST Compliance</p>
              <h2 className="ap-title">File-ready reports. Every month.</h2>
              <p className="ap-lede">
                EasyBilling doesn't just create invoices — it organises your GST data
                automatically so filing is never a last-minute scramble. GSTR-1, GSTR-3B,
                and HSN Summary are always current and ready to export.
              </p>
              <div className="ap-tags">
                <span className="ap-tag">GSTR-1</span>
                <span className="ap-tag">GSTR-3B</span>
                <span className="ap-tag">HSN Summary</span>
                <span className="ap-tag ap-tag--amber">E-Invoice soon</span>
              </div>
            </div>
            <div className="ap-gst-art ap-reveal" style={{ ...d(120), background: "#fff" }}>
              <img src={gstReports} alt="GST reports overview" loading="lazy" />
            </div>
          </div>
          <div className="ap-gst-list">
            {GST_FEATURES.map((f, i) => (
              <div className="ap-gst-item ap-reveal" style={d((i % 3) * 70)} key={f.label}>
                <span className="ap-check"><Check size={11} /></span>
                <div>
                  <h4>{f.label}</h4>
                  <p>{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Who it's for ────────────────────────────────────────────────── */}
      <section className="ap-section">
        <div className="ap-container">
          <div className="ap-head ap-reveal">
            <p className="ap-eyebrow">Who it's for</p>
            <h2 className="ap-title">Built for every kind of business.</h2>
          </div>
          <div className="ap-grid ap-grid--4">
            {WHO.map((w, i) => (
              <article className="ap-card ap-card--hover ap-reveal" style={d(i * 80)} key={w.title}>
                <div className="ap-art" style={{ background: "#fff" }}><img src={w.art} alt="" loading="lazy" /></div>
                <h3 className="ap-card-t">{w.title}</h3>
                <p className="ap-card-d">{w.desc}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ── Testimonials ────────────────────────────────────────────────── */}
      <section className="ap-section ap-section--alt">
        <div className="ap-container">
          <div className="ap-head ap-reveal">
            <p className="ap-eyebrow">Testimonials</p>
            <h2 className="ap-title">Trusted by businesses across India.</h2>
          </div>
          <div className="ap-grid ap-grid--3">
            {TESTIMONIALS.map((t, i) => (
              <figure className="ap-card ap-quote ap-reveal" style={d(i * 80)} key={t.name}>
                <blockquote>“{t.quote}”</blockquote>
                <figcaption className="ap-quote-who">
                  <span className="ap-avatar">{t.initials}</span>
                  <span>
                    <div className="ap-quote-name">{t.name}</div>
                    <div className="ap-quote-role">{t.role}, {t.company}</div>
                  </span>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* ── Comparison ──────────────────────────────────────────────────── */}
      <section className="ap-section ap-section--alt" style={{ paddingTop: 0 }}>
        <div className="ap-container">
          <div className="ap-head ap-reveal">
            <p className="ap-eyebrow">Why EasyBilling</p>
            <h2 className="ap-title ap-title--sm">Simpler than Tally.<br />More powerful than spreadsheets.</h2>
          </div>
          <div className="ap-compare ap-reveal" role="table" aria-label="Feature comparison">
            <div className="ap-compare-row ap-compare-head" role="row">
              <div role="columnheader">Feature</div>
              <div role="columnheader" className="is-us">EasyBilling</div>
              <div role="columnheader">Tally</div>
              <div role="columnheader">Spreadsheets</div>
            </div>
            {COMPARISON.map(([feat, ...vals]) => (
              <div className="ap-compare-row" role="row" key={feat}>
                <div className="ap-compare-feat" role="cell">{feat}</div>
                {vals.map((v, j) => (
                  <div className="ap-compare-cell" role="cell" key={j}>
                    {v
                      ? <span className={`ap-yes${j === 0 ? " is-us" : ""}`} aria-label="Yes"><Check size={16} w={2} /></span>
                      : <span className="ap-no" aria-label="No" />}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ─────────────────────────────────────────────────────────── */}
      <section className="ap-cta">
        <img className="ap-cta-art" src={ctaBanner} alt="" aria-hidden="true" loading="lazy" />
        <div className="ap-container ap-cta-inner">
          <h2 className="ap-title ap-reveal">Ready to simplify your billing?</h2>
          <p className="ap-lede ap-reveal" style={d(80)}>
            Join hundreds of Indian businesses already using EasyBilling.
            Free to start. No credit card required.
          </p>
          <div className="ap-hero-ctas ap-reveal" style={d(140)}>
            <button className="ap-btn ap-btn--light" onClick={() => navigate("/register")}>Create your free account</button>
            <button className="ap-btn ap-btn--ghost-dark" onClick={() => navigate("/login")}>Sign in <Chevron /></button>
          </div>
        </div>
      </section>

      {/* ── FAQ ─────────────────────────────────────────────────────────── */}
      <section id="faq" className="ap-section">
        <div className="ap-container">
          <div className="ap-head ap-reveal">
            <p className="ap-eyebrow">FAQ</p>
            <h2 className="ap-title ap-title--sm">Common questions.</h2>
          </div>
          <div className="ap-faq">
            {FAQS.map((f, i) => <FAQItem key={i} id={i} {...f} />)}
          </div>
        </div>
      </section>

      {/* ── Footer ──────────────────────────────────────────────────────── */}
      <footer className="ap-footer">
        <div className="ap-container">
          <div className="ap-footer-top">
            <div className="ap-footer-blurb">
              <div className="ap-brand" style={{ cursor: "default" }}>
                <span className="ap-brand-mark">EB</span>
                <span className="ap-brand-name" style={{ fontSize: 15 }}>EasyBilling</span>
              </div>
              <p>GST-compliant invoicing and business management for Indian SMBs. Built in India, for India.</p>
            </div>
            <div className="ap-footer-cols">
              {[
                { title: "Product", links: [["Features", "#features"], ["How it works", "#how"], ["GST Compliance", "#gst"], ["FAQ", "#faq"]] },
                { title: "Legal", links: [["Privacy Policy", "/privacy"], ["Terms of Service", "/terms"]] },
                { title: "Account", links: [["Sign In", "/login"], ["Register", "/register"]] },
              ].map(col => (
                <div className="ap-footer-col" key={col.title}>
                  <h4>{col.title}</h4>
                  <ul>
                    {col.links.map(([label, path]) => (
                      <li key={label}><a href={path}>{label}</a></li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
          <div className="ap-footer-bottom">
            <span>© {new Date().getFullYear()} EasyBilling. All rights reserved.</span>
            <span>Made with ♥ in India · GST compliant</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
